import {LOCAL_API_ERROR_HANDLER, LocalApiErrorHandler} from '../shared/errors/local-api-error-handler';
import {UnitOptionDto} from './UnitOptionDto';
export type {UnitOptionDto} from './UnitOptionDto';
import {StockFactsDto, mapStockFacts} from '../shared/components/sm-stock-summary/stock-summary.model';
import {SmPackagingValue} from "../shared/form-controls/sm-quantity-packaging/quantity-packaging.model";
import {ItemPrice, ItemPriceDto, mapItemPrice} from '../shared/prices/currency.service';
import type {FavoriteState} from '../favorites/favorite-state';
import {HttpClient, HttpContext} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {map, Observable, tap} from 'rxjs';
import {Store} from '@ngrx/store';
import {ApiResponse} from '../auth/auth.models';
import {buildApiUrl} from '../shared/api/api-url';
import {PACKAGING_ERROR_CODES} from '../shared/packaging/packaging-correction.service';
import {LOCALLY_HANDLED_API_ERROR_CODES} from '../shared/errors/local-api-error';
import {ReportsActions} from '../reports/store/reports.actions';
import {PublicationModerationService} from '../moderation/publication-moderation.service';

export type ItemImageDto = {
  id: string;
  originalUrl: string;
  thumbnailUrl: string;
  url: string;
  isMain: boolean;
  useInAi: boolean;
};



function toUnitOption(dto: UnitOptionDto): UnitOptionDto {
  return {
    id: dto.id,
    isDefault: dto.isDefault,
    isActive: dto.isActive,
    allowedPackagingUnitIds: dto.allowedPackagingUnitIds ?? null,
    isQuantityUnit: dto.isQuantityUnit === true,
    supportsPackaging: dto.supportsPackaging === true,
    isPackagingUnit: dto.isPackagingUnit === true,
    isSplittable: dto.isSplittable === true,
    kind: dto.kind ?? null,
    baseUnitId: dto.baseUnitId ?? null,
    baseUnitFactor: dto.baseUnitFactor ?? null,
    name: dto.name,
    shortName: dto.shortName,
  };
}

export type ItemListItemDto = StockFactsDto & FavoriteState & {
  groupId?: string | null;
  localGroupId?: string | null;
  group?: ItemGroupSummaryDto | null;
  unitId?: string | null;
  unitLabel?: string | null;
  description: string | null;
  expirationDate: string | null;
  expirationStatus: string;
  id: string;
  imageUrl: string | null;
  images: ItemImageDto[];
  name: string;
  noticeBeforeDays: number | null;
  quantity: number;
};

type ItemByLocationResponseDto = {
  availableUnits?: UnitOptionDto[];
  items?: ItemListItemDto[];
};

type ItemGroupSummaryDto = {
  remainingContent?: number | null;
  contentCapacity?: number | null;
  packagingUnitLabel?: string | null;
  hasPackaging?: boolean;
  groupId: string;
  localGroupId: string;
  quantity: string;
  unitId: string | null;
  unitLabel: string | null;
  itemCount: number;
};

export type ItemGroupSummary = {
  remainingContent?: number | null;
  contentCapacity?: number | null;
  packagingUnitLabel?: string | null;
  hasPackaging?: boolean;
  groupId: string;
  localGroupId: string;
  quantity: string;
  unitId: string | null;
  unitLabel: string | null;
  itemCount: number;
};

export type ItemPathSegment = {
  id: string;
  name: string;
};

export type ItemDetails = StockFactsDto & FavoriteState & {
  updatedAt?: string;
  packagingQuantity?: number | null;
  packagingUnitId?: string | null;
  groupId?: string | null;
  localGroupId?: string | null;
  price?: ItemPriceDto | null;
  canManagePrice?: boolean;
  barcode?: string | null;
  assignedQrCode: {id: string; token: string} | null;
  containerPath: ItemPathSegment[];
  description: string | null;
  expirationDate: string | null;
  expirationStatus: string;
  canEdit: boolean;
  sharedRole: string | null;
  imageUrl: string | null;
  images: ItemImageDto[];
  name: string;
  noticeBeforeDays: number | null;
  quantity: number;
  unitLabel: string | null;
  unitId: string | null;
};

export type ItemWriteOffResult = {
  containerId: string;
  description: string | null;
  id: string;
  imageUrl: string | null;
  name: string;
  quantity: number;
  unitId: string | null;
};

export type ItemRelocateResult = {
  destinationLocationId: string;
  id: string;
  sourceLocationId: string;
};

export type ItemUpdateRequest = {
  packaging?: SmPackagingValue | null;
  price?: ItemPrice | null;
  barcode?: string;
  description: string;
  expirationDate: string;
  images: ItemImageUploadRequest[];
  name: string;
  allowQrCodeReassignment?: boolean;
  qrCodeToken: string;
  removeAssignedQrCode: boolean;
  removeImage?: boolean;
  noticeBeforeDays: string;
  quantity: string;
  unitId: string;
};

export type ItemIncrementRequest = {expectedQuantity: number; expectedUnitId: string; quantity: number};

export type ItemSplitRequest = {
  operationId: string;
  expectedUpdatedAt: string;
  qrCodeTokens: string[];
  item: ItemUpdateRequest;
};

type ItemSplitResponseDto = {operationId: string; itemIds: string[]; remainingQuantity: number};
export type ItemSplitResult = {operationId: string; itemIds: string[]; remainingQuantity: number};

export type ItemImageUploadRequest = {
  file?: File;
  id?: string;
  isMain: boolean;
  useInAi: boolean;
};

export type ItemEditContext = StockFactsDto & {
  packagingQuantity?: number | null;
  packagingUnitId?: string | null;
  groupId?: string | null;
  localGroupId?: string | null;
  updatedAt?: string;
  price?: ItemPriceDto | null;
  canManagePrice?: boolean;
  barcode?: string | null;
  assignedQrCode: {id: string; token: string} | null;
  availableUnits: UnitOptionDto[];
  clonedFromItemId: string | null;
  description: string | null;
  expirationDate: string | null;
  imageUrl: string | null;
  images: ItemImageDto[];
  name: string;
  noticeBeforeDays: number | null;
  quantity: number;
  unitId: string | null;
  canManagePublication: boolean;
  publication: ItemPublication;
};

export type ReplenishmentTracking = {
  itemId: string; productGroupId: string; containerId: string; trackingEnabled: boolean;
  minimumQuantity: number | null; desiredQuantity: number | null; version: number;
};

export type ItemPublicationStatus = 'NotRequested' | 'Pending' | 'Approved' | 'Rejected' | 'Revoked' | 'Disabled';

export type ItemPublication = {
  status: ItemPublicationStatus;
  rejectionReason: string | null;
  requestedAt: string | null;
  approvedAt: string | null;
  rejectedAt: string | null;
  revokedAt: string | null;
  isPublicPreviewEnabled: boolean;
};

type ItemActivityEventBase = {
  actorUserId: string;
  actorUsername: string;
  created: Date;
  id: string;
  itemId: string;
  payloadVersion: number;
};

export type ItemActivityEvent = ItemActivityEventBase & (
  | {type: 'created'; data: ItemActivityCreatedData}
  | {type: 'relocated'; data: ItemActivityRelocatedData}
  | {type: 'writtenOff'; data: ItemActivityQuantityData}
  | {type: 'quantityAdjusted'; data: ItemActivityQuantityData}
  | {type: 'updated'; data: ItemActivityUpdatedData}
  | {type: 'unknown'; data: Record<string, unknown>}
);

export type ItemActivityCreatedData = {
  name: string;
  description: string | null;
  quantity: number;
  unitName: string | null;
  unitShortName: string | null;
  expirationDate: string | null;
  noticeBeforeDays: number | null;
  locationPath: string;
  imageCount: number;
};

export type ItemActivityRelocatedData = {
  previousPath: string;
  newPath: string;
};

export type ItemActivityQuantityData = {
  mode?: string;
  contentWrittenOff?: number;
  remainingContentBefore?: number;
  remainingContentAfter?: number;
  packagingQuantity?: number;
  quantityAfter: number;
  quantityBefore: number;
  quantityDelta: number;
  unitName: string | null;
  unitShortName: string | null;
};

export type ItemActivityValueChange<T> = {previous: T; current: T};

export type ItemActivityUpdatedData = {
  name: ItemActivityValueChange<string> | null;
  description: ItemActivityValueChange<string | null> | null;
  quantity: (ItemActivityValueChange<number> & {
    previousUnitName: string | null;
    previousUnitShortName: string | null;
    currentUnitName: string | null;
    currentUnitShortName: string | null;
  }) | null;
  unit: {
    previousName: string | null;
    previousShortName: string | null;
    currentName: string | null;
    currentShortName: string | null;
  } | null;
  expirationDate: ItemActivityValueChange<string | null> | null;
  noticeBeforeDays: ItemActivityValueChange<number | null> | null;
  images: {added: number; changed: number; removed: number} | null;
};

export type ItemActivityHistoryFilter =
  | 'all'
  | 'Created'
  | 'Relocated'
  | 'WrittenOff'
  | 'QuantityAdjusted'
  | 'Updated';

export type ItemActivityHistoryPage = {
  events: ItemActivityEvent[];
  page: number;
  pageSize: number;
  totalCount: number;
};

export type ItemCreateContext = {
  canManagePrice?: boolean;
  availableUnits: UnitOptionDto[];
};

export type ItemGenerateSuggestionRequest = {
  existingImageIds?: string[];
  existingImageUrls?: string[];
  images?: File[];
  itemId?: string;
};

export type ItemGenerateSuggestionResponseDto = {
  model: string;
  suggestedDescription: string;
  suggestedExpirationDate?: string | null;
  suggestedName: string | null;
};

export type ItemCreateImageRequest = {
  file?: File;
  id?: string;
  isMain: boolean;
  useInAi: boolean;
};

export type ItemCreateRequest = {
  packaging?: SmPackagingValue | null;
  price?: ItemPrice | null;
  barcode?: string;
  description: string;
  expirationDate: string;
  clonedFromItemId?: string | null;
  copiedImageSelectionSpecified?: boolean;
  copyImagesFromItemId?: string | null;
  images: ItemCreateImageRequest[];
  name: string;
  noticeBeforeDays: string;
  allowQrCodeReassignment?: boolean;
  qrCodeToken: string;
  quantity: string;
  requestPublication: boolean;
  unitId: string;
};

export type QrCodeAssignmentStatus = 'unassigned' | 'assignedToContainer' | 'assignedToItem';

export type ResolvedQrCode = {
  assignedItemId: string | null;
  assignedItemName: string | null;
  assignedLocationId: string | null;
  assignedLocationName: string | null;
  id: string;
  status: QrCodeAssignmentStatus;
  token: string;
};

export type ItemListData = {
  availableUnits: UnitOptionDto[];
  items: ItemListItem[];
};

export type ItemListItem = StockFactsDto & FavoriteState & {
  groupId?: string | null;
  localGroupId?: string | null;
  group?: ItemGroupSummary | null;
  unitId?: string | null;
  unitLabel?: string | null;
  description: string | null;
  expirationDate: string | null;
  expirationStatus: string;
  id: string;
  imageUrl: string | null;
  images: ItemImageDto[];
  name: string;
  noticeBeforeDays: number | null;
  quantity: number;
};

type ItemGetResponseDto = StockFactsDto & FavoriteState & {
  updatedAt?: string;
  packagingQuantity?: number | null;
  packagingUnitId?: string | null;
  groupId?: string | null;
  localGroupId?: string | null;
  price?: ItemPriceDto | null;
  canManagePrice?: boolean;
  barcode?: string | null;
  assignedQrCode?: {id: string; token: string} | null;
  containerPath?: ItemPathSegment[];
  description: string | null;
  expirationDate: string | null;
  expirationStatus: string;
  canEdit: boolean;
  sharedRole: string | null;
  imageUrl: string | null;
  images?: ItemImageDto[];
  name: string;
  noticeBeforeDays: number | null;
  quantity: number;
  unitLabel: string | null;
  unitId: string | null;
};


type ItemRelocateResponseDto = {
  destinationLocationId: string;
  id: string;
  sourceLocationId: string;
};

type ItemUpdateResponseDto = Record<string, never>;

type QrCodeResolveResponseDto = {
  assignedItemId: string | null;
  assignedItemName: string | null;
  assignedLocationId: string | null;
  assignedLocationName: string | null;
  id: string;
  status: 'Unassigned' | 'AssignedToContainer' | 'AssignedToItem';
  token: string;
};

type ItemEditContextResponseDto = StockFactsDto & {
  packagingQuantity?: number | null;
  packagingUnitId?: string | null;
  groupId?: string | null;
  localGroupId?: string | null;
  updatedAt?: string;
  price?: ItemPriceDto | null;
  canManagePrice?: boolean;
  barcode?: string | null;
  assignedQrCode?: {id: string; token: string} | null;
  availableUnits?: UnitOptionDto[];
  clonedFromItemId: string | null;
  description: string | null;
  expirationDate: string | null;
  imageUrl: string | null;
  images?: ItemImageDto[];
  name: string;
  noticeBeforeDays: number | null;
  quantity: number;
  unitId: string | null;
  canManagePublication: boolean;
  publication: ItemPublicationDto;
};

type ItemPublicationDto = {
  status: ItemPublicationStatus;
  rejectionReason: string | null;
  requestedAt: string | null;
  approvedAt: string | null;
  rejectedAt: string | null;
  revokedAt: string | null;
  isPublicPreviewEnabled: boolean;
};

type ItemCreateContextResponseDto = {
  canManagePrice?: boolean;
  availableUnits?: UnitOptionDto[];
};

type ItemCreateResponseDto = {
  id: string;
};

type ItemActivityValueChangeDto<T> = {
  Previous: T;
  Current: T;
};

type ItemActivityCreatedDataDto = {
  Name: string;
  Description: string | null;
  Quantity: number;
  UnitName: string | null;
  UnitShortName: string | null;
  ExpirationDate: string | null;
  NoticeBeforeDays: number | null;
  LocationPath: string;
  ImageCount: number;
};

type ItemActivityRelocatedDataDto = {
  PreviousPath: string;
  NewPath: string;
};

type ItemActivityQuantityDataDto = {
  Mode?: string;
  ContentWrittenOff?: number;
  RemainingContentBefore?: number;
  RemainingContentAfter?: number;
  PackagingQuantity?: number;
  QuantityAfter: number;
  QuantityBefore: number;
  QuantityDelta: number;
  UnitName: string | null;
  UnitShortName: string | null;
};

type ItemActivityUpdatedDataDto = {
  Name: ItemActivityValueChangeDto<string> | null;
  Description: ItemActivityValueChangeDto<string | null> | null;
  Quantity: (ItemActivityValueChangeDto<number> & {
    PreviousUnitName: string | null;
    PreviousUnitShortName: string | null;
    CurrentUnitName: string | null;
    CurrentUnitShortName: string | null;
  }) | null;
  Unit: {
    PreviousName: string | null;
    PreviousShortName: string | null;
    CurrentName: string | null;
    CurrentShortName: string | null;
  } | null;
  ExpirationDate: ItemActivityValueChangeDto<string | null> | null;
  NoticeBeforeDays: ItemActivityValueChangeDto<number | null> | null;
  Images: {Added: number; Changed: number; Removed: number} | null;
};

type ItemActivityEventDto = {
  ActorUserId: string;
  ActorUsername: string;
  Created: string;
  Data: Record<string, unknown>;
  EventType: 'Created' | 'Relocated' | 'WrittenOff' | 'QuantityAdjusted' | 'Updated' | string;
  Id: string;
  ItemId: string;
  PayloadVersion: number;
};

type ItemActivityHistoryResponseDto = {
  Events: ItemActivityEventDto[];
  Page: number;
  PageSize: number;
  TotalCount: number;
};

function toItemImage(dto: ItemImageDto): ItemImageDto {
  return {
    id: dto.id,
    originalUrl: dto.originalUrl,
    thumbnailUrl: dto.thumbnailUrl,
    url: dto.url,
    isMain: dto.isMain,
    useInAi: dto.useInAi,
  };
}

function toItemListItem(dto: ItemListItemDto): ItemListItem {
  return {
    ...mapStockFacts(dto),
    groupId: dto.groupId ?? null,
    localGroupId: dto.localGroupId ?? null,
    group: dto.group ? {
      remainingContent: dto.group.remainingContent, contentCapacity: dto.group.contentCapacity,
      packagingUnitLabel: dto.group.packagingUnitLabel, hasPackaging: dto.group.hasPackaging,
      groupId: dto.group.groupId,
      localGroupId: dto.group.localGroupId,
      quantity: dto.group.quantity,
      unitId: dto.group.unitId,
      unitLabel: dto.group.unitLabel,
      itemCount: dto.group.itemCount,
    } : null,
    unitId: dto.unitId ?? null,
    unitLabel: dto.unitLabel ?? null,
    isFavorite: dto.isFavorite,
    description: dto.description,
    expirationDate: dto.expirationDate,
    expirationStatus: dto.expirationStatus,
    id: dto.id,
    imageUrl: dto.imageUrl,
    images: (dto.images ?? []).map(toItemImage),
    name: dto.name,
    noticeBeforeDays: dto.noticeBeforeDays,
    quantity: dto.quantity,
  };
}

function toItemDetails(dto: ItemGetResponseDto): ItemDetails {
  return {
    updatedAt: dto.updatedAt,
    ...mapStockFacts(dto),
    groupId: dto.groupId ?? null,
    localGroupId: dto.localGroupId ?? null,
    isFavorite: dto.isFavorite,
    barcode: dto.barcode ?? null,
    packagingQuantity: dto.packagingQuantity ?? null,
    packagingUnitId: dto.packagingUnitId ?? null,
    price: mapItemPrice(dto.price),
    canManagePrice: dto.canManagePrice === true,
    assignedQrCode: dto.assignedQrCode ?? null,
    containerPath: (dto.containerPath ?? []).map((segment) => ({
      id: segment.id,
      name: segment.name,
    })),
    description: dto.description,
    expirationDate: dto.expirationDate,
    expirationStatus: dto.expirationStatus,
    canEdit: dto.canEdit,
    sharedRole: dto.sharedRole,
    imageUrl: dto.imageUrl,
    images: (dto.images ?? []).map(toItemImage),
    name: dto.name,
    noticeBeforeDays: dto.noticeBeforeDays,
    quantity: dto.quantity,
    unitLabel: dto.unitLabel,
    unitId: dto.unitId,
  };
}


function toItemEditContext(dto: ItemEditContextResponseDto): ItemEditContext {
  return {
    ...mapStockFacts(dto),
    groupId: dto.groupId ?? null,
    localGroupId: dto.localGroupId ?? null,
    updatedAt: dto.updatedAt,
    barcode: dto.barcode ?? null,
    packagingQuantity: dto.packagingQuantity ?? null,
    packagingUnitId: dto.packagingUnitId ?? null,
    price: mapItemPrice(dto.price),
    canManagePrice: dto.canManagePrice === true,
    assignedQrCode: dto.assignedQrCode ?? null,
    availableUnits: (dto.availableUnits ?? []).map(toUnitOption),
    clonedFromItemId: dto.clonedFromItemId,
    description: dto.description,
    expirationDate: dto.expirationDate,
    imageUrl: dto.imageUrl,
    images: (dto.images ?? []).map(toItemImage),
    name: dto.name,
    noticeBeforeDays: dto.noticeBeforeDays,
    quantity: dto.quantity,
    unitId: dto.unitId,
    canManagePublication: dto.canManagePublication,
    publication: toItemPublication(dto.publication),
  };
}

function toItemPublication(dto: ItemPublicationDto): ItemPublication {
  return {
    status: dto.status,
    rejectionReason: dto.rejectionReason,
    requestedAt: dto.requestedAt,
    approvedAt: dto.approvedAt,
    rejectedAt: dto.rejectedAt,
    revokedAt: dto.revokedAt,
    isPublicPreviewEnabled: dto.isPublicPreviewEnabled,
  };
}

function toItemCreateContext(dto: ItemCreateContextResponseDto): ItemCreateContext {
  return {
    canManagePrice: dto.canManagePrice === true,
    availableUnits: (dto.availableUnits ?? []).map(toUnitOption),
  };
}

function toItemActivityEvent(dto: ItemActivityEventDto): ItemActivityEvent {
  const base: ItemActivityEventBase = {
    actorUserId: dto.ActorUserId,
    actorUsername: dto.ActorUsername,
    created: new Date(dto.Created),
    id: dto.Id,
    itemId: dto.ItemId,
    payloadVersion: dto.PayloadVersion,
  };

  switch (dto.EventType) {
    case 'Created': {
      const data = dto.Data as ItemActivityCreatedDataDto;
      return { ...base, type: 'created', data: {
        name: data.Name,
        description: data.Description,
        quantity: data.Quantity,
        unitName: data.UnitName,
        unitShortName: data.UnitShortName,
        expirationDate: data.ExpirationDate,
        noticeBeforeDays: data.NoticeBeforeDays,
        locationPath: data.LocationPath,
        imageCount: data.ImageCount,
      }};
    }
    case 'Relocated': {
      const data = dto.Data as ItemActivityRelocatedDataDto;
      return {...base, type: 'relocated', data: {previousPath: data.PreviousPath, newPath: data.NewPath}};
    }
    case 'WrittenOff':
    case 'QuantityAdjusted': {
      const data = dto.Data as ItemActivityQuantityDataDto;
      return {...base, type: dto.EventType === 'WrittenOff' ? 'writtenOff' : 'quantityAdjusted', data: {
        mode: data.PackagingQuantity != null ? data.Mode : undefined, contentWrittenOff: data.ContentWrittenOff,
        remainingContentBefore: data.RemainingContentBefore, remainingContentAfter: data.RemainingContentAfter,
        packagingQuantity: data.PackagingQuantity,
        quantityAfter: data.QuantityAfter,
        quantityBefore: data.QuantityBefore,
        quantityDelta: data.QuantityDelta,
        unitName: data.UnitName,
        unitShortName: data.UnitShortName,
      }};
    }
    case 'Updated': {
      const data = dto.Data as ItemActivityUpdatedDataDto;
      return {...base, type: 'updated', data: {
        name: data.Name ? {previous: data.Name.Previous, current: data.Name.Current} : null,
        description: data.Description
          ? {previous: data.Description.Previous, current: data.Description.Current}
          : null,
        quantity: data.Quantity ? {
          previous: data.Quantity.Previous,
          current: data.Quantity.Current,
          previousUnitName: data.Quantity.PreviousUnitName,
          previousUnitShortName: data.Quantity.PreviousUnitShortName,
          currentUnitName: data.Quantity.CurrentUnitName,
          currentUnitShortName: data.Quantity.CurrentUnitShortName,
        } : null,
        unit: data.Unit ? {
          previousName: data.Unit.PreviousName,
          previousShortName: data.Unit.PreviousShortName,
          currentName: data.Unit.CurrentName,
          currentShortName: data.Unit.CurrentShortName,
        } : null,
        expirationDate: data.ExpirationDate
          ? {previous: data.ExpirationDate.Previous, current: data.ExpirationDate.Current}
          : null,
        noticeBeforeDays: data.NoticeBeforeDays
          ? {previous: data.NoticeBeforeDays.Previous, current: data.NoticeBeforeDays.Current}
          : null,
        images: data.Images
          ? {added: data.Images.Added, changed: data.Images.Changed, removed: data.Images.Removed}
          : null,
      }};
    }
    default:
      return {...base, type: 'unknown', data: dto.Data};
  }
}

function toResolvedQrCode(dto: QrCodeResolveResponseDto): ResolvedQrCode {
  return {
    assignedItemId: dto.assignedItemId,
    assignedItemName: dto.assignedItemName,
    assignedLocationId: dto.assignedLocationId,
    assignedLocationName: dto.assignedLocationName,
    id: dto.id,
    status: dto.status === 'AssignedToItem'
      ? 'assignedToItem'
      : dto.status === 'AssignedToContainer'
        ? 'assignedToContainer'
        : 'unassigned',
    token: dto.token,
  };
}

@Injectable({
  providedIn: 'root',
})
export class ItemsService {
  constructor(
    private readonly httpClient: HttpClient,
    private readonly store: Store,
    private readonly publicationModerationService: PublicationModerationService,
  ) {}

  getByLocationId(locationId: string, visibility: 'withoutZeroCount' | 'withZeroCount' | 'all' = 'withoutZeroCount'): Observable<ItemListData> {
    return this.httpClient.get<ApiResponse<ItemByLocationResponseDto>>(
      buildApiUrl(`/v1/item-containers/${locationId}/items`), {params: {visibility}}
    ).pipe(
      map((payload) => ({
        availableUnits: (payload.data?.availableUnits ?? []).map(toUnitOption),
        items: (payload.data?.items ?? []).map(toItemListItem),
      })),
    );
  }

  resolveQrCode(token: string): Observable<ResolvedQrCode> {
    return this.httpClient.get<ApiResponse<QrCodeResolveResponseDto>>(
      buildApiUrl(`/v1/qr-codes/${encodeURIComponent(token)}`),
      {context: new HttpContext().set(LOCALLY_HANDLED_API_ERROR_CODES, ['RecordNotFound'])}
    ).pipe(
      map((payload) => {
        if (!payload.data) {
          throw new Error('QR code resolve response is missing.');
        }

        return toResolvedQrCode(payload.data);
      }),
    );
  }

  getItem(locationId: string, itemId: string, errorHandler: LocalApiErrorHandler | null = null): Observable<ItemDetails> {
    return this.httpClient.get<ApiResponse<ItemGetResponseDto>>(
      buildApiUrl(`/v1/item-containers/${locationId}/items/${itemId}`),
      {context: new HttpContext().set(LOCAL_API_ERROR_HANDLER, errorHandler)},
    ).pipe(
      map((payload) => {
        if (!payload.data) {
          throw new Error('Item details response is missing.');
        }

        return toItemDetails(payload.data);
      }),
    );
  }

  getItemEditContext(locationId: string, itemId: string, errorHandler: LocalApiErrorHandler | null = null): Observable<ItemEditContext> {
    return this.httpClient.get<ApiResponse<ItemEditContextResponseDto>>(
      buildApiUrl(`/v1/item-containers/${locationId}/items/${itemId}/edit-context`),
      {context: new HttpContext().set(LOCAL_API_ERROR_HANDLER, errorHandler)},
    ).pipe(
      map((payload) => {
        if (!payload.data) {
          throw new Error('Item edit context response is missing.');
        }

        return toItemEditContext(payload.data);
      }),
    );
  }

  getReplenishmentTracking(itemId: string): Observable<ReplenishmentTracking> {
    return this.httpClient.get<ApiResponse<ReplenishmentTracking>>(
      buildApiUrl(`/v1/me/basket/tracking/items/${itemId}`)
    ).pipe(map(payload => {
      if (!payload.data) throw new Error('Replenishment tracking response is missing.');
      return payload.data;
    }));
  }

  updateReplenishmentTracking(itemId: string, enabled: boolean,
    minimumQuantity: number | null, desiredQuantity: number | null, version: number): Observable<ReplenishmentTracking> {
    return this.httpClient.put<ApiResponse<ReplenishmentTracking>>(
      buildApiUrl(`/v1/me/basket/tracking/items/${itemId}`),
      {enabled, minimumQuantity, desiredQuantity, version}
    ).pipe(map(payload => {
      if (!payload.data) throw new Error('Replenishment tracking response is missing.');
      return payload.data;
    }));
  }

  previewWriteOff(request: ContentWriteOffPreviewRequest): Observable<ContentWriteOffResult> {
    return this.httpClient.post<ApiResponse<ContentWriteOffResult>>(buildApiUrl('/v1/inventory-write-off/calculate'), request, {context: new HttpContext().set(LOCALLY_HANDLED_API_ERROR_CODES, ['BadRequest', 'ConcurrentModification', 'Conflict'])}).pipe(
      map(response => {
        if (!response.success || !response.data) throw new Error(response.error?.message || 'Write-off preview failed');
        return {...mapStockFacts(response.data), ...response.data};
      }),
    );
  }

  writeOff(request: ContentWriteOffRequest): Observable<ContentWriteOffResult> {
    return this.httpClient.post<ApiResponse<ContentWriteOffResult>>(buildApiUrl('/v1/inventory-write-off'), request, {context: new HttpContext().set(LOCALLY_HANDLED_API_ERROR_CODES, ['BadRequest', 'ConcurrentModification', 'Conflict'])}).pipe(
      map(response => {
        if (!response.success || !response.data) throw new Error(response.error?.message || 'Write-off failed');
        return {...mapStockFacts(response.data), ...response.data};
      }),
      tap(() => this.dispatchMutation(request.itemId, 'writtenOff')),
    );
  }

  relocateItem(locationId: string, itemId: string, destinationLocationId: string): Observable<ItemRelocateResult> {
    return this.httpClient.post<ApiResponse<ItemRelocateResponseDto>>(
      buildApiUrl(`/v1/item-containers/${locationId}/items/${itemId}/relocate`),
      {DestinationLocationId: destinationLocationId},
    ).pipe(
      map((payload) => {
        if (!payload.data) {
          throw new Error('Item relocation response is missing.');
        }

        return {
          destinationLocationId: payload.data.destinationLocationId,
          id: payload.data.id,
          sourceLocationId: payload.data.sourceLocationId,
        };
      }),
      tap(() => this.dispatchMutation(itemId, 'moved')),
    );
  }

  deleteItem(locationId: string, itemId: string): Observable<void> {
    return this.httpClient.delete<ApiResponse<Record<string, never>>>(
      buildApiUrl(`/v1/item-containers/${locationId}/items/${itemId}`),
    ).pipe(
      map(() => void 0),
      tap(() => this.dispatchMutation(itemId, 'deleted')),
    );
  }

  getItemCreateContext(locationId: string): Observable<ItemCreateContext> {
    return this.httpClient.get<ApiResponse<ItemCreateContextResponseDto>>(
      buildApiUrl(`/v1/item-containers/${locationId}/items/create-context`)
    ).pipe(
      map((payload) => {
        if (!payload.data) {
          throw new Error('Item create context response is missing.');
        }

        return toItemCreateContext(payload.data);
      }),
    );
  }

  getItemActivityHistory(
    locationId: string,
    itemId: string,
    page = 1,
    pageSize = 20,
    eventType?: Exclude<ItemActivityHistoryFilter, 'all'>,
    dateFrom?: string,
    dateTo?: string,
    errorHandler: LocalApiErrorHandler | null = null,
  ): Observable<ItemActivityHistoryPage> {
    const params: Record<string, string | number> = {page, pageSize};
    if (eventType) {
      params['eventType'] = eventType;
    }
    if (dateFrom) {
      params['dateFrom'] = dateFrom;
    }
    if (dateTo) {
      params['dateTo'] = dateTo;
    }

    return this.httpClient.get<ApiResponse<ItemActivityHistoryResponseDto>>(
      buildApiUrl(`/v1/item-containers/${locationId}/items/${itemId}/activity-history`),
      {params, context: new HttpContext().set(LOCAL_API_ERROR_HANDLER, errorHandler)},
    ).pipe(
      map((payload) => ({
        events: (payload.data?.Events ?? []).map(toItemActivityEvent),
        page: payload.data?.Page ?? page,
        pageSize: payload.data?.PageSize ?? pageSize,
        totalCount: payload.data?.TotalCount ?? 0,
      })),
    );
  }

  generateItemDescriptionSuggestion(
    locationId: string,
    request: ItemGenerateSuggestionRequest,
  ): Observable<ItemGenerateSuggestionResponseDto> {
    const formData = new FormData();

    const itemId = request.itemId?.trim();
    if (itemId) {
      formData.append('ItemId', itemId);
    }

    for (const existingImageId of request.existingImageIds ?? []) {
      const normalized = existingImageId.trim();
      if (normalized) {
        formData.append('ExistingImageIds', normalized);
      }
    }

    for (const existingImageUrl of request.existingImageUrls ?? []) {
      const normalized = existingImageUrl.trim();
      if (normalized) {
        formData.append('ExistingImageUrls', normalized);
      }
    }

    for (const image of request.images ?? []) {
      formData.append('Images', image);
    }

    return this.httpClient.post<ApiResponse<ItemGenerateSuggestionResponseDto>>(
      buildApiUrl(`/v1/item-containers/${locationId}/items/ai-suggestion`),
      formData,
      {context: new HttpContext().set(LOCALLY_HANDLED_API_ERROR_CODES, ['VALIDATION_ERROR', 'AiCreditsRequired', 'AiProviderNotConnected'])},
    ).pipe(
      map((payload) => {
        if (!payload.data) {
          throw new Error('Generated item suggestion response is missing.');
        }

        return payload.data;
      }),
    );
  }

  createItem(locationId: string, request: ItemCreateRequest): Observable<string> {
    return this.httpClient.post<ApiResponse<ItemCreateResponseDto>>(
      buildApiUrl(`/v1/item-containers/${locationId}/items`),
      this.buildCreateForm(request),
      {context: new HttpContext().set(LOCALLY_HANDLED_API_ERROR_CODES,
        ['VALIDATION_ERROR', 'QrCodeAlreadyAssigned', 'QrCodeOwnedByAnotherUser', ...PACKAGING_ERROR_CODES])},
    ).pipe(
      map((payload) => {
        if (!payload.data?.id) {
          throw new Error('Created item response is missing.');
        }

        return payload.data.id;
      }),
      tap((itemId) => {
        this.dispatchMutation(itemId, request.clonedFromItemId ? 'cloned' : 'created');
        if (request.requestPublication) {
          this.publicationModerationService.incrementPendingCount();
        }
      }),
    );
  }

  updateItem(locationId: string, itemId: string, request: ItemUpdateRequest): Observable<void> {
    return this.httpClient.put<ApiResponse<ItemUpdateResponseDto>>(
      buildApiUrl(`/v1/item-containers/${locationId}/items/${itemId}`), this.buildUpdateForm(request),
      {context: new HttpContext().set(LOCALLY_HANDLED_API_ERROR_CODES,
        ['VALIDATION_ERROR', 'QrCodeAlreadyAssigned', 'QrCodeOwnedByAnotherUser', ...PACKAGING_ERROR_CODES])},
    ).pipe(map(() => void 0), tap(() => this.dispatchMutation(itemId, 'updated')));
  }

  incrementItem(locationId: string, itemId: string, request: ItemIncrementRequest): Observable<void> {
    return this.httpClient.post<ApiResponse<object>>(
      buildApiUrl(`/v1/item-containers/${locationId}/items/${itemId}/increment`), request,
      {context: new HttpContext().set(LOCALLY_HANDLED_API_ERROR_CODES, ['Conflict'])},
    ).pipe(map(() => void 0), tap(() => this.dispatchMutation(itemId, 'updated')));
  }

  checkSplitQrCode(locationId: string, itemId: string, token: string): Observable<'Available' | 'Invalid' | 'Unavailable'> {
    return this.httpClient.get<ApiResponse<{status: 'Available' | 'Invalid' | 'Unavailable'}>>(
      buildApiUrl(`/v1/item-containers/${locationId}/items/${itemId ? itemId + '/split' : 'instances'}/qr-check`), {params: {token}}
    ).pipe(map(response => {
      if (!response.data || !['Available', 'Invalid', 'Unavailable'].includes(response.data.status)) {
        throw new Error('Invalid QR validation response');
      }
      return response.data.status;
    }));
  }

  splitItem(locationId: string, itemId: string, request: ItemSplitRequest): Observable<ItemSplitResult> {
    const form = this.buildUpdateForm(request.item);
    form.append('operationId', request.operationId);
    if (itemId) form.append('expectedUpdatedAt', request.expectedUpdatedAt);
    request.qrCodeTokens.forEach(token => form.append('qrCodeTokens', token.trim()));
    return this.httpClient.post<ApiResponse<ItemSplitResponseDto>>(
      buildApiUrl(`/v1/item-containers/${locationId}/items/${itemId ? itemId + '/split' : 'instances'}`), form,
      {context: new HttpContext().set(LOCALLY_HANDLED_API_ERROR_CODES, ['VALIDATION_ERROR', 'Conflict', 'ConcurrentModification', 'QrCodeAlreadyAssigned'])},
    ).pipe(
      map(response => {
        if (!response.data) throw new Error('Split response is missing.');
        return {operationId: response.data.operationId, itemIds: [...response.data.itemIds],
          remainingQuantity: response.data.remainingQuantity};
      }),
      tap(result => this.dispatchMutation(itemId || result.itemIds[0], itemId ? 'updated' : 'created')),
    );
  }


  private buildCreateForm(request: ItemCreateRequest): FormData {
    const formData = new FormData();
    this.appendCreateImages(formData, request);
    formData.append('Name', request.name.trim());
    formData.append('Description', request.description.trim());
    formData.append('Quantity', request.quantity.trim());
    formData.append('UnitId', request.unitId.trim());
    if (request.packaging !== undefined) {
      formData.append('packagingSpecified', 'true');
      if (request.packaging) {
        formData.append('packagingQuantity', request.packaging.quantity.trim().replace(',', '.'));
        formData.append('packagingUnitId', request.packaging.unitId);
      }
    }

    const expirationDate = request.expirationDate.trim();
    if (expirationDate.length > 0) {
      formData.append('ExpirationDate', expirationDate);
    }

    appendPrice(formData, request.price);
    if (request.barcode !== undefined) {
      formData.append('barcode', request.barcode.trim());
    }

    const qrCodeToken = request.qrCodeToken.trim();
    if (qrCodeToken.length > 0) {
      formData.append('QrCodeToken', qrCodeToken);
    }

    if (request.allowQrCodeReassignment) {
      formData.append('AllowQrCodeReassignment', 'true');
    }

    formData.append('RequestPublication', String(request.requestPublication));

    const noticeBeforeDays = request.noticeBeforeDays.trim();
    if (noticeBeforeDays.length > 0) {
      formData.append('NoticeBeforeDays', noticeBeforeDays);
    }

    return formData;
  }

  private appendCreateImages(formData: FormData, request: ItemCreateRequest): void {
    if (request.clonedFromItemId) {
      formData.append('ClonedFromItemId', request.clonedFromItemId);
    }

    const copiedImages = request.images.filter((image) => Boolean(image.id?.trim()) && !image.file);
    if (request.copyImagesFromItemId) {
      formData.append('CopyImagesFromItemId', request.copyImagesFromItemId);

      if (request.copiedImageSelectionSpecified) {
        formData.append('CopiedImageSelectionSpecified', 'true');
      }

      copiedImages.forEach((image, index) => {
        formData.append(`CopiedImageFlags[${index}].Id`, image.id!.trim());
        formData.append(`CopiedImageFlags[${index}].IsMain`, String(image.isMain));
        formData.append(`CopiedImageFlags[${index}].UseInAi`, String(image.useInAi));
      });
    }

    let imageIndex = 0;
    for (const image of request.images) {
      if (!image.file) {
        continue;
      }

      formData.append('Images', image.file);
      formData.append(`NewImageFlags[${imageIndex}].IsMain`, String(image.isMain));
      formData.append(`NewImageFlags[${imageIndex}].UseInAi`, String(image.useInAi));
      imageIndex++;
    }

  }

  private buildUpdateForm(request: ItemUpdateRequest): FormData {
    const formData = new FormData();
    formData.append('Name', request.name.trim());
    formData.append('Quantity', request.quantity.trim());
    formData.append('UnitId', request.unitId.trim());
    if (request.packaging !== undefined) {
      formData.append('packagingSpecified', 'true');
      if (request.packaging) {
        formData.append('packagingQuantity', request.packaging.quantity.trim().replace(',', '.'));
        formData.append('packagingUnitId', request.packaging.unitId);
      }
    }
    formData.append('Description', request.description.trim());

    const expirationDate = request.expirationDate.trim();
    if (expirationDate.length > 0) {
      formData.append('ExpirationDate', expirationDate);
    }

    appendPrice(formData, request.price);
    if (request.barcode !== undefined) {
      formData.append('barcode', request.barcode.trim());
    }

    const qrCodeToken = request.qrCodeToken.trim();
    if (qrCodeToken.length > 0) {
      formData.append('QrCodeToken', qrCodeToken);
    }

    if (request.allowQrCodeReassignment) {
      formData.append('AllowQrCodeReassignment', 'true');
    }

    if (request.removeAssignedQrCode) {
      formData.append('RemoveAssignedQrCode', 'true');
    }

    if (request.removeImage) {
      formData.append('RemoveImage', 'true');
    }

    const existingImages = request.images.filter((image) => Boolean(image.id) && !image.file);
    const newImages = request.images.filter((image) => Boolean(image.file));

    existingImages.forEach((image, index) => {
      formData.append('ExistingImageIds', image.id!.trim());
      formData.append(`ExistingImageFlags[${index}].Id`, image.id!.trim());
      formData.append(`ExistingImageFlags[${index}].IsMain`, String(image.isMain));
      formData.append(`ExistingImageFlags[${index}].UseInAi`, String(image.useInAi));
    });

    newImages.forEach((image, index) => {
      if (image.file) {
        formData.append('Images', image.file);
      }

      formData.append(`NewImageFlags[${index}].IsMain`, String(image.isMain));
      formData.append(`NewImageFlags[${index}].UseInAi`, String(image.useInAi));
    });

    const noticeBeforeDays = request.noticeBeforeDays.trim();
    if (noticeBeforeDays.length > 0) {
      formData.append('NoticeBeforeDays', noticeBeforeDays);
    }

    return formData;
  }

  requestItemPublication(locationId: string, itemId: string): Observable<ItemPublication> {
    return this.httpClient.put<ApiResponse<ItemPublicationDto>>(
      buildApiUrl(`/v1/item-containers/${locationId}/items/${itemId}/publication`),
      {Action: 'Request'},
    ).pipe(
      map((payload) => {
        if (!payload.data) {
          throw new Error('Publication response is missing.');
        }

        return toItemPublication(payload.data);
      }),
      tap(() => this.publicationModerationService.incrementPendingCount()),
    );
  }

  private dispatchMutation(
    entityId: string,
    operation: 'created' | 'updated' | 'deleted' | 'cloned' | 'writtenOff' | 'moved',
  ): void {
    this.store.dispatch(ReportsActions.inventoryMutationCompleted({
      entity: 'item',
      entityId,
      operation,
    }));
  }
}

function appendPrice(form: FormData, price: ItemPrice | null | undefined): void {
  if (price === undefined) return;
  if (price === null) { form.append('clearPrice', 'true'); return; }
  form.append('price.amount', price.amount.toString());
  form.append('price.currencyCode', price.currencyCode);
  form.append('price.basisQuantity', price.basisQuantity.toString());
  form.append('price.basisUnitId', price.basisUnitId);
}

export type InventoryAmount = {amount: number; unitId: string | null};
export type InventoryQuantity = {primary: InventoryAmount; additionalContent?: InventoryAmount | null};
export type ContentWriteOffPreviewRequest = {expectedUpdatedAt: string; itemId: string; locationId: string;
  sourceKind: 'item' | 'container'; quantity: InventoryQuantity};
/** operationId is an idempotency key for one confirmed operation, reused for network retries. */
export type ContentWriteOffRequest = ContentWriteOffPreviewRequest & {operationId: string};
export type ContentWriteOffResult = StockFactsDto & {operationId: string; id: string; containerId: string; unitId?: string | null;
  quantity: number; updatedAt: string; contentWrittenOff: number | null; before: InventoryQuantity; writtenOff: InventoryQuantity; after: InventoryQuantity};
